// THE WORDS THE STAGED SURFACE WRITES ITSELF (T-129; REQ-101 AC1, AC4, AC5, REQ-281 AC4).
//
// "Staged" is the product's word for a change that is decided and not yet sent — never "change set",
// "changeset", "draft", "pending" or "queued", none of which say what has and has not happened. SC-5 scans
// the rendered screen for the jargon, and `staged.glossary.test.tsx` scans this table on its own.
//
// The consequence sentences, the impact figures and the failures are NOT here: they are the server's, and
// they arrive on `ChangeItem.consequence` / `.impact` / `.failure` through the catalogue (ARCH §2.7).
// What is here is the frame — the pill, the marker's accessible name, the announcement, the reminder.
//
// The phone says the same things (T-138) and its parity test reads this file as text.

export const STAGED_COPY = {
  // ---- the shell pill (REQ-101 AC4) --------------------------------------------------------------------
  /** "3 staged" — the count of what is waiting, on every screen. */
  pill: (n: string, one: boolean): string => (one ? `${n} staged` : `${n} staged`),
  /** BUG-098 (w07 ①, m06 ③): the staged set's net effect on daily spend, beside the count where it fits. */
  pillNet: (amount: string): string => `· ${amount} per day`,
  /**
   * What the pill is and what pressing it does, for the control's accessible name. **T-132 made it a
   * door**: until then it was a fact with `aria-disabled` and `pillNotYet` beside it, because the MVP never
   * shows a dead control (DESIGN_SYSTEM §10). It opens the tray now, so the name says so.
   */
  pillLabel: (n: string, one: boolean): string =>
    one
      ? `1 staged change, waiting to be published. Open the tray`
      : `${n} staged changes, waiting to be published. Open the tray`,

  // ---- the row and header marker (REQ-101 AC1) ---------------------------------------------------------
  /** The accessible name of a staged value: what it will be, what it is now, and that it has not happened. */
  markerLabel: (to: string, from: string): string =>
    `Staged: ${to}, was ${from}. Not published yet.`,
  /** The visually-hidden word that precedes the struck-through original for a screen reader. */
  markerWas: 'was',
  /** BUG-099 (§12 no. 9, D-209): the ONE pill for a change that is not published yet — the clock, and the word. */
  stagedWord: 'Staged',
  /** …and in View-only §12.2's own word: a change that is planned, not staged for a publish. */
  plannedWord: 'Planned',
  /** A staged turn-off / turn-on, where the value is a switch rather than an amount. */
  statusOn: 'On',
  statusOff: 'Off',

  // ---- the announcement (REQ-281 AC4) ------------------------------------------------------------------
  /** Said when an edit is added to the tray. The count travels with it, because the pill may be off-screen. */
  announceAdded: (what: string, n: string): string => `Staged: ${what}. ${n} waiting to publish.`,
  /** Said when an edit replaces one already staged on the same thing (API §6.3 upserts on entity + field). */
  announceMerged: (what: string, n: string): string => `Staged: ${what}. ${n} waiting to publish.`,
  /** Said when an edit is taken back off, either explicitly or by setting the value back to the live one. */
  announceRemoved: (what: string, n: string): string =>
    `No longer staged: ${what}. ${n} waiting to publish.`,
  /** The tail of every announcement above, as its own string so the count is written once. */
  waiting: (n: string, one: boolean): string => (one ? '1 change' : `${n} changes`),
  /** "Cart Abandoners 14d, budget $30" — the subject of an announcement, never a sentence of consequence. */
  announceSubject: (name: string, what: string): string => `${name}, ${what}`,
  /**
   * The subject with the SERVER's own consequence sentence after it (T-131). A flip that needed no confirm
   * was never read before it was staged, so the one thing the reader has not seen is what it does to the
   * money — and by the time the announcement is built `validateChange` has already said it. The sentence is
   * still not ours: this joins two strings and writes neither.
   */
  announceWith: (subject: string, consequence: string): string => `${subject} — ${consequence}`,
  announceBudget: (amount: string): string => `budget ${amount}`,
  announceStatusOn: 'turned on',
  announceStatusOff: 'turned off',

  // ---- the 24-hour reminder (REQ-101 AC5) --------------------------------------------------------------
  /**
   * One gentle band on the next visit, naming the count and how old the oldest one is. It is a reminder,
   * not a warning: nothing is wrong, nothing has expired, and nothing will happen on its own.
   */
  reminderTitle: (n: string, one: boolean): string =>
    one ? 'You have 1 staged change waiting' : `You have ${n} staged changes waiting`,
  reminderBody: (age: string): string =>
    `The oldest has been waiting ${age}. Nothing has been sent to Meta — they stay here until you publish them.`,
  reminderDismiss: 'Not now',
  /** "a day" · "2 days" — the age, in the coarse words a reminder wants. */
  reminderAgeDays: (n: number): string => (n === 1 ? 'a day' : `${n} days`),
} as const;

// THE INLINE BUDGET EDITOR (T-130; REQ-102). The words around the server's, and no others: the impact
// figures, the validation sentences, the consequence and the warnings are all `Message`s the engine
// decided (ENGINE §11) and the catalogue renders — this table is the frame, the buttons and the labels.
export const BUDGET_EDIT_COPY = {
  /** The cell's own control: "Edit the budget for Cart Abandoners 14d". */
  trigger: (name: string): string => `Edit the budget for ${name}`,
  /** The entity page's own button — a labelled control, because the page has room for words. */
  editHere: 'Change it',
  title: 'Daily budget',
  titleTotal: 'Total budget',
  /** The field's label — visually hidden; the panel's title says which budget it is. */
  fieldLabel: 'Amount',
  /** BUG-105 / §12 no. 3: the affix is part of the AMOUNT — it is what stops "$40" reading as a total. */
  perDaySuffix: 'per day',
  lifetimeSuffix: 'in total',
  /** Four quick steps, REQ-102 AC1. The sign is the label: "−20%" is what it does, not what it is. */
  step: (pct: number): string => `${pct > 0 ? '+' : '−'}${Math.abs(pct)}%`,
  stepLabel: (pct: number, amount: string): string =>
    `${pct > 0 ? 'Raise' : 'Lower'} by ${Math.abs(pct)} per cent, to ${amount}`,
  stepsLabel: 'Quick steps',
  /** While the 150 ms debounce is out and the first answer has not landed. */
  checking: 'Working out what that would do…',
  /** REQ-102 AC2's three windows, each named. The figures are the server's. */
  impactPerDay: (delta: string): string => `${delta} per day`,
  impactRestOfMonth: (delta: string, month: string): string => `${delta} for the rest of ${month}`,
  impact30d: (delta: string): string => `${delta} over the next 30 days`,
  /** Nothing moves: the typed value is the one that is already set. */
  impactNone: 'The same as it is now.',
  /** REQ-102 AC4 — the safer value becomes the PRIMARY button when there is one (D-064, D-180 (3)). */
  useSafer: (amount: string): string => `Use ${amount} instead`,
  stageAnyway: (amount: string): string => `Stage ${amount} anyway`,
  stage: 'Stage this change',
  cancel: 'Cancel',
  /** REQ-102 AC5 — an ad set under a shared campaign budget is not edited here. */
  sharedTitle: 'This budget is set on the campaign',
  sharedBody:
    'Its campaign holds one budget and spreads it across its ad sets, so there is nothing to change here.',
  sharedLink: "Edit the campaign's budget",
  /** The editor could not reach the server; the value is untouched and nothing was staged. */
  checkFailed: "We couldn't check that amount just now. Nothing has been staged.",
  /** Said when the typed value equals the live one and something was staged — the change is taken back. */
  removed: 'Back to the amount that is set — the staged change is gone.',
} as const;

// THE ON / OFF SWITCH (T-131; REQ-103, REQ-140 AC6). DESIGN_SYSTEM §12.1's words and no others: the
// consequence sentence, every warning and every refusal are the server's (ENGINE §11 through API §6.2) and
// arrive as `Message`s the catalogue renders. What is here is the control's name, the confirm's frame and
// its two buttons.
//
// **The words are Turn off / Turn on** (D-035, REQ-045) — never Pause / Resume, which SC-5 bans outright,
// and never "toggle" or "enable", which say what the widget is instead of what it does.
export const SWITCH_COPY = {
  /** The switch's own name: what activating it would DO, and to what. */
  turnOff: (name: string): string => `Turn off ${name}`,
  turnOn: (name: string): string => `Turn on ${name}`,
  /**
   * The switch already carries a staged flip, so activating it takes that back — the tray's own verb
   * ("Remove this change", `components.html` row 20). The switch still shows where the account really is,
   * so "Turn on" here would name a state it is already in.
   */
  removeStaged: (name: string): string => `Remove the staged change for ${name}`,
  /**
   * WCAG 2.5.3: where the switch shows a word of its own ("On"), the accessible name has to begin with it.
   * In a table the label is dropped (§12.1 item 1) and the action stands alone.
   */
  nameWithState: (state: string, action: string): string => `${state} — ${action}`,
  /** While `validateChange` is out: the thumb spins and input is blocked (§12.1 item 5). */
  checking: 'Working out what that would do…',

  // ---- the confirm (REQ-103 AC3; `components.html` §05, DESIGN_SYSTEM §12.1 item 4) ------------------
  confirmOff: (name: string): string => `Turn off “${name}”?`,
  confirmOn: (name: string): string => `Turn on “${name}”?`,
  /** The safe default is the button that changes nothing; on a high-stakes flip it also takes the focus. */
  keepOn: 'Keep it on',
  keepOff: 'Leave it off',
  stage: 'Stage this change',
  /** Under the buttons, once: a confirm that looks like a commitment has to say that it is not one. */
  notYet: 'Nothing is sent to Meta until you publish.',

  // ---- taking a staged flip back (the only way to, until the tray lands — T-132) ---------------------
  removeTitle: 'Remove the staged change?',
  removeBody: (name: string, stagedWord: string): string =>
    `${stagedWord} is staged for ${name}. Nothing has been sent to Meta, so removing it leaves ${name} exactly as it is now.`,
  keepStaged: 'Keep it staged',
  remove: 'Remove it',
  /** The staged value, as a word — the same two the marker uses. */
  wordOn: 'On',
  wordOff: 'Off',

  // ---- the check could not be made: nothing was staged and the switch has not moved -----------------
  checkFailedTitle: 'We could not check that',
  checkFailed:
    "We couldn't work out what that would do just now, so nothing has been staged and nothing has changed. Try again in a moment.",
} as const;

// THE TRAY (T-132; REQ-101 AC2, AC6; `mockups/web/w07-review-publish.html` step 2). The frame around the
// server's own words: a row reads **"Entity — what changes — from → to"** (AC2's sentence, literally), and
// under it the money the engine worked out. Nothing here is a consequence, a figure or a refusal — those
// arrive on the `ChangeItem` and are rendered through the catalogue.
export const TRAY_COPY = {
  title: 'Staged changes',
  /** The dialog's own name, which says how many are in it so a screen reader hears it once. */
  label: (n: string, one: boolean): string =>
    one ? 'Staged changes: 1 change' : `Staged changes: ${n} changes`,
  close: 'Close',
  /** REQ-101 AC2's second control. Destructive, so it asks once more (`discardTitle` below). */
  discardAll: 'Discard all',
  /**
   * REQ-101 AC6 — **"Review n changes" in every mode**, never "Publish" and never "Review & publish",
   * which would promise something a View-only account cannot do.
   */
  review: (n: string, one: boolean): string => (one ? 'Review 1 change' : `Review ${n} changes`),
  /**
   * The review screen is T-133. The button is drawn with its real words and `aria-disabled` until then —
   * the same bargain T-129 made for the pill, and DESIGN_SYSTEM §10's rule for a control that is half
   * built: say why rather than open nothing. T-133 turns it into a link and deletes this line.
   */
  reviewNotYet: 'The review screen arrives next — nothing is sent to Meta until you publish.',
  /** The foot, under the list: what the tray promises. */
  kept: 'Kept until you publish or discard.',
  /** BUG-098 (w07 ①): the set's net effect on daily spend, said once on the foot. */
  net: (amount: string): string => `about ${amount} per day overall`,
  /** BUG-098 (w07 ②): the pencil on a budget row — it reopens that row's editor on the tree. */
  edit: (summary: string): string => `Edit: ${summary}`,

  // ---- one row (REQ-101 AC2: "Entity — what changes — from → to") -------------------------------------
  /** What the change is TO, in the owner's words — never `daily_budget`, which is Meta's field name. */
  fieldDaily: 'Daily budget',
  fieldTotal: 'Total budget',
  fieldStatus: 'On or off',
  /** "Clearance – Lookalike 3% — On or off · On → Off" — the row's whole sentence, in one element. */
  summary: (entity: string, field: string, from: string, to: string): string =>
    `${entity} — ${field} · ${from} → ${to}`,
  /** The unit the pair is in, said once after it rather than twice inside it. */
  perDay: 'per day',
  /** The money, the mockup's own lead: "about −$22 per day". */
  impactPerDay: (delta: string): string => `about ${delta} per day`,
  /** Nothing moves — the mockup's words for a change that only re-spends a budget somewhere else. */
  noSpendChange: 'no spend change',
  /** The × on a row. It names the change, because a row of ×s all read the same otherwise. */
  remove: (summary: string): string => `Remove this change: ${summary}`,
  removing: 'Removing…',
  /** A published value moved under a staged one (REQ-105 AC3): said in words, never as two raw values. */
  conflict: (stagedFrom: string, current: string): string =>
    `This was staged from ${stagedFrom}, and it is ${current} now. Review it before publishing.`,

  // ---- "Discard all", asked once more (it throws away every decision in the tray) ----------------------
  discardTitle: (n: string, one: boolean): string =>
    one ? 'Discard the staged change?' : `Discard all ${n} staged changes?`,
  discardBody:
    'Nothing has been sent to Meta, so your ads do not change — but the edits themselves are gone and cannot be brought back.',
  discardCancel: 'Keep them',
  discardConfirm: 'Discard all',
  discardWorking: 'Discarding…',

  // ---- nothing staged (REQ-287): reachable by removing the last one with the tray open -----------------
  emptyTitle: 'Nothing staged',
  emptyBody:
    'Edits collect here before anything happens. Change a budget or turn something off, and it waits here until you publish it.',
} as const;

// THE RECOMMENDED ACTION (T-137; REQ-074 AC3, REQ-075 AC2). The button that turns the product's own
// suggestion into a staged change — on a "Money in vs results out" pair, on an allocation block and on a
// possibly-wasted row.
//
// **The LABEL is never here.** "Raise to $30 per day", "Turn off this ad set" — those are the engine's
// (`recommended-actions.json` → `action.*` in the catalogue), arrive on `RecommendedAction.label`, and are
// rendered whole. A client that writes its own action label is one release away from offering a step the
// engine never proposed, which is exactly what D-013 and T-086 (6) forbid. What is here is the frame: what
// the button says about ITSELF once the change is staged, and what the row promises underneath.
export const ACTION_COPY = {
  /**
   * The accessible name once the suggestion HAS been staged. The button keeps the engine's label as its
   * visible text — the staged fact is the `StagedMark` beside it, where every other surface puts it — and
   * this says what pressing it does now. It is deliberately NOT `aria-pressed`: the control is not a
   * toggle, and "pressed" would promise that pressing it again takes the change back. It opens the tray,
   * which is the one place a change is read or removed (REQ-101 AC2).
   */
  stagedHint: (label: string): string => `${label} — staged; open the staged changes`,
  /** While `validateChange` is out. */
  checking: 'Working out what that would do…',
  /**
   * Under the action, once per row: the promise REQ-074 AC3 and REQ-075 AC2 both rest on. The mockup's own
   * sentence (w06 `.w06-flag__then`) — a button beside a figure about money has to say that pressing it
   * does not spend any.
   */
  then: 'Stages the change for review — it never acts by itself.',
} as const;
